import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { Product } from "@/types/product";
import { supabase } from "@/integrations/supabase/client";
import { resolveVisitorGeo, getCachedVisitorGeo } from "@/lib/visitor-geo";

// Get or create a guest session id (persists across visits in localStorage)
function getGuestSessionId(): string {
  try {
    let sid = localStorage.getItem("padelcorner-session-id");
    if (!sid) {
      sid = `guest_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("padelcorner-session-id", sid);
    }
    return sid;
  } catch {
    return `guest_${Date.now()}`;
  }
}

type CartActionType = "add" | "remove" | "update" | "clear";

async function logCartActivity(params: {
  action: CartActionType;
  product?: Product;
  variantId?: string;
  variantLabel?: string;
  quantity?: number;
  previousQuantity?: number;
  unitPrice?: number;
}) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const geo = getCachedVisitorGeo() ?? (await resolveVisitorGeo());
    await supabase.from("cart_activity_log").insert({
      action: params.action,
      session_id: getGuestSessionId(),
      user_id: user?.id ?? null,
      user_email: user?.email ?? null,
      product_id: params.product?.id ?? null,
      product_name: params.product?.name ?? null,
      product_sku: (params.product as any)?.sku ?? null,
      variant_id: params.variantId ?? null,
      variant_label: params.variantLabel ?? null,
      quantity: params.quantity ?? 0,
      previous_quantity: params.previousQuantity ?? null,
      unit_price: params.unitPrice ?? params.product?.price ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      page_url: typeof window !== "undefined" ? window.location.href : null,
      country: geo.country,
      country_code: geo.country_code,
    });
  } catch (err) {
    // Silent fail — never block cart UX on logging
    console.warn("[cart-log] failed", err);
  }
}

export interface CartItem {
  product: Product;
  quantity: number;
  variantId?: string;
  variantLabel?: string;
  variantPrice?: number;
}

export interface AppliedPromo {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  discountAmount: number; // calculated amount
  scopeType: string;
  scopeIds: string[];
  excludeSaleItems: boolean;
  excludeDeliveryFee?: boolean;
}

export interface AppliedGiftCard {
  code: string;
  remainingAmount: number;
  currency: string;
  redeemedAmount: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, qty?: number, variant?: { id: string; label: string; price?: number }) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, qty: number, variantId?: string) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  appliedPromo: AppliedPromo | null;
  setAppliedPromo: (promo: AppliedPromo | null) => void;
  discountAmount: number;
  bundleDiscount: number;
  setBundleDiscount: (n: number) => void;
  appliedGiftCard: AppliedGiftCard | null;
  setAppliedGiftCard: (card: AppliedGiftCard | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function matchesKey(item: CartItem, productId: string, variantId?: string): boolean {
  if (variantId) return item.product.id === productId && item.variantId === variantId;
  return item.product.id === productId && !item.variantId;
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("padelcorner-cart") || localStorage.getItem("2move-cart");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [appliedPromo, setAppliedPromoState] = useState<AppliedPromo | null>(() => {
    try {
      const saved = localStorage.getItem("padelcorner-promo");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [appliedGiftCard, setAppliedGiftCardState] = useState<AppliedGiftCard | null>(() => {
    try {
      const saved = localStorage.getItem("padelcorner-giftcard");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("padelcorner-cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (appliedPromo) {
      localStorage.setItem("padelcorner-promo", JSON.stringify(appliedPromo));
    } else {
      localStorage.removeItem("padelcorner-promo");
    }
  }, [appliedPromo]);

  useEffect(() => {
    if (appliedGiftCard) {
      localStorage.setItem("padelcorner-giftcard", JSON.stringify(appliedGiftCard));
    } else {
      localStorage.removeItem("padelcorner-giftcard");
    }
  }, [appliedGiftCard]);

  // Re-validate applied promo whenever cart items change (subtotal, contents).
  // Ensures discount updates or clears if items no longer qualify.
  const _currentSubtotalForPromo = items.reduce((sum, i) => {
    const price = i.variantPrice ?? i.product.price;
    return sum + price * i.quantity;
  }, 0);
  const _itemsKey = items
    .map((i) => `${i.product.id}:${i.variantId ?? ""}:${i.quantity}:${i.variantPrice ?? i.product.price}`)
    .join("|");

  useEffect(() => {
    if (!appliedPromo) return;
    if (items.length === 0) {
      setAppliedPromoState(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("validate-promo", {
          body: {
            code: appliedPromo.code,
            subtotal: _currentSubtotalForPromo,
            items: items.map((i) => ({
              product_id: i.product.id,
              category_id: (i.product as any).category_id || null,
              product_type: (i.product as any).product_type || null,
              price: i.variantPrice ?? i.product.price,
              compare_at_price: (i.product as any).compareAtPrice ?? null,
              quantity: i.quantity,
            })),
          },
        });
        if (cancelled) return;
        if (error || !data || data.error) {
          setAppliedPromoState(null);
          return;
        }
        const newAmount = Number(data.discount_amount) || 0;
        if (Math.abs(newAmount - appliedPromo.discountAmount) > 0.001) {
          setAppliedPromoState({
            code: data.code,
            discountType: data.discount_type,
            discountValue: Number(data.discount_value),
            discountAmount: newAmount,
            scopeType: data.scope_type,
            scopeIds: data.scope_ids || [],
            excludeSaleItems: data.exclude_sale_items || false,
            excludeDeliveryFee: data.exclude_delivery_fee || false,
          });
        }
      } catch {
        // keep on transient failure
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_itemsKey, appliedPromo?.code]);

  const setAppliedPromo = useCallback((promo: AppliedPromo | null) => {
    setAppliedPromoState(promo);
  }, []);

  const setAppliedGiftCard = useCallback((card: AppliedGiftCard | null) => {
    setAppliedGiftCardState(card);
  }, []);

  const addToCart = useCallback((product: Product, qty = 1, variant?: { id: string; label: string; price?: number }) => {
    let prevQty = 0;
    setItems((prev) => {
      const existing = prev.find((i) => matchesKey(i, product.id, variant?.id));
      if (existing) {
        prevQty = existing.quantity;
        return prev.map((i) =>
          matchesKey(i, product.id, variant?.id) ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, {
        product,
        quantity: qty,
        variantId: variant?.id,
        variantLabel: variant?.label,
        variantPrice: variant?.price,
      }];
    });
    setIsCartOpen(true);
    logCartActivity({
      action: "add",
      product,
      variantId: variant?.id,
      variantLabel: variant?.label,
      quantity: prevQty + qty,
      previousQuantity: prevQty,
      unitPrice: variant?.price ?? product.price,
    });
  }, []);

  const removeFromCart = useCallback((productId: string, variantId?: string) => {
    setItems((prev) => {
      const target = prev.find((i) => matchesKey(i, productId, variantId));
      if (target) {
        logCartActivity({
          action: "remove",
          product: target.product,
          variantId: target.variantId,
          variantLabel: target.variantLabel,
          quantity: 0,
          previousQuantity: target.quantity,
          unitPrice: target.variantPrice ?? target.product.price,
        });
      }
      return prev.filter((i) => !matchesKey(i, productId, variantId));
    });
  }, []);

  const updateQuantity = useCallback((productId: string, qty: number, variantId?: string) => {
    setItems((prev) => {
      const target = prev.find((i) => matchesKey(i, productId, variantId));
      if (qty <= 0) {
        if (target) {
          logCartActivity({
            action: "remove",
            product: target.product,
            variantId: target.variantId,
            variantLabel: target.variantLabel,
            quantity: 0,
            previousQuantity: target.quantity,
            unitPrice: target.variantPrice ?? target.product.price,
          });
        }
        return prev.filter((i) => !matchesKey(i, productId, variantId));
      }
      if (target && target.quantity !== qty) {
        logCartActivity({
          action: "update",
          product: target.product,
          variantId: target.variantId,
          variantLabel: target.variantLabel,
          quantity: qty,
          previousQuantity: target.quantity,
          unitPrice: target.variantPrice ?? target.product.price,
        });
      }
      return prev.map((i) => (matchesKey(i, productId, variantId) ? { ...i, quantity: qty } : i));
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems((prev) => {
      if (prev.length > 0) {
        logCartActivity({ action: "clear", quantity: 0, previousQuantity: prev.reduce((s, i) => s + i.quantity, 0) });
      }
      return [];
    });
    setAppliedPromoState(null);
    setAppliedGiftCardState(null);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => {
    const price = i.variantPrice ?? i.product.price;
    return sum + price * i.quantity;
  }, 0);

  // Calculate discount amount based on applied promo
  const discountAmount = appliedPromo ? Math.min(appliedPromo.discountAmount, subtotal) : 0;

  const [bundleDiscount, setBundleDiscount] = useState(0);

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, subtotal, isCartOpen, setIsCartOpen, appliedPromo, setAppliedPromo, discountAmount, bundleDiscount, setBundleDiscount, appliedGiftCard, setAppliedGiftCard }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
